import throttle from 'lodash/throttle.js';
import { connect, DB_NAME, METADATA_STORE_NAME, waitForRequest, } from './IndexedDBStore.js';
/**
 * IndexedDB-backed twin of MetaDataStore. Same `load`/`get`/`set` contract, but
 * the recovery snapshot lives in IndexedDB instead of localStorage so large
 * Transloadit assemblies don't blow past localStorage's ~5MB quota (issue #6280).
 *
 * `get` stays synchronous (served from an in-memory cache) because it runs on
 * every state update; only loading and persisting touch IndexedDB.
 */
export default class IndexedDBMetaDataStore {
    #db;
    #cache;
    #key;
    #expires;
    #saveThrottled;
    constructor(opts) {
        this.#db = connect(DB_NAME);
        // Attach a handler now so a connection failure can't surface as an
        // unhandledrejection before load()/#save() get a chance to await it; both
        // of those handle the rejection themselves (persistence is best-effort).
        this.#db.catch(() => { });
        this.#key = opts.storeName;
        this.#expires = opts.expires;
        this.#saveThrottled =
            opts.throttleTime === 0
                ? this.#save
                : throttle(this.#save, opts.throttleTime ?? 500, {
                    leading: true,
                    trailing: true,
                });
    }
    async load() {
        try {
            const db = await this.#db;
            const record = await waitForRequest(db
                .transaction([METADATA_STORE_NAME])
                .objectStore(METADATA_STORE_NAME)
                .get(this.#key));
            if (!record || record.expires < Date.now())
                return undefined;
            this.#cache = JSON.parse(record.metadata);
            return this.#cache ?? undefined;
        }
        catch {
            // Best-effort: a corrupt record, failed read, or unavailable DB must
            // never break restore — mirrors the localStorage path's tolerant parse.
            return undefined;
        }
    }
    get() {
        return this.#cache ?? undefined;
    }
    set(metadata) {
        this.#cache = metadata;
        this.#saveThrottled();
    }
    #save = async () => {
        try {
            const db = await this.#db;
            const store = db
                .transaction([METADATA_STORE_NAME], 'readwrite')
                .objectStore(METADATA_STORE_NAME);
            if (this.#cache == null) {
                store.delete(this.#key);
            }
            else {
                // JSON.stringify (not raw structured clone) so non-cloneable values are
                // dropped instead of throwing. See the StateRecord comment above.
                store.put({
                    id: this.#key,
                    expires: Date.now() + this.#expires,
                    metadata: JSON.stringify(this.#cache),
                });
            }
        }
        catch {
            // Persistence is best-effort; a failed write must never break uploading.
        }
    };
}
